import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsList from "@/components/CardsList";
import HandleMedicalRecordModal from "@/pages/Medical/Modals/handleRecord";

import {MedicalAPI} from "@/api/medical";

import AddIcon from "@mui/icons-material/Add";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {useCategories, usePlayers} from "@/utils/hooks";

import "./index.scss";

function MedicalRecordsPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [records, setRecords] = useState([]);

	const [openModal, setOpenModal] = useState(false);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const {players} = usePlayers({
		setLoading: false,
		handleNotification: undefined,
		goalkeepers: false,
		onlyInternal: true,
		prospect: false,
	});

	const getRecords = async () => {
		setLoading(true);
		try {
			let response = await MedicalAPI.recordsList();
			response = response.map((r) => {
				r.category = r.playerData?.category;
				return r;
			});
			setRecords(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getRecords();
	}, []);

	const saveRecord = async (data) => {
		setLoading(true);
		setOpenModal(false);
		try {
			const response = await MedicalAPI.recordCreate({
				playerId: data?.player?.value,
				notes: "",
			});
			navigate(`/medical/records/${response?.id}`);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(true);
		}
	};

	const groupedRecords = {};
	categories
		.filter((c) => c.name !== "Prima Squadra" && c.name !== "Scuola Portieri")
		.forEach((c) => {
			const currentCategory = c.name;
			const members = records
				?.filter((r) => r.playerData.category === currentCategory)
				?.map((r) => {
					return {
						...r,
						playerLastName: r.playerData?.lastName,
					};
				});
			groupedRecords[currentCategory] = members;
		});

	const getListConfiguration = () => {
		return {
			data: groupedRecords,
			onClick: (record) => navigate("/medical/records/" + record.id),
			imageUrl: (item) => require("@/assets/images/medical/record.png"),
			title: (record) =>
				record?.playerData
					? record?.playerData?.firstName + " " + record?.playerData?.lastName
					: "---",
			subtitle: (record) =>
				record?.playerData
					? moment(record?.playerData.dateOfBirth).format("YYYY")
					: "---",
		};
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			onClick: () => {
				setOpenModal(true);
			},
		},
	];

	const recordsPlayerIds = records?.map((r) => r.playerId) || [];

	return (
		<Page>
			<Loading visible={loading || !players?.length} />
			<Breadcrumbs actions={<MainActions actions={mainActions} />} />
			<CardsList
				grouped
				groupSorted={false}
				type="medical_record"
				listConfiguration={getListConfiguration()}
				defaultSortedField="playerLastName"
				defaultSortedOrder="ascend"
			/>
			{openModal ? (
				<HandleMedicalRecordModal
					open={openModal}
					confirm={saveRecord}
					close={() => setOpenModal(false)}
					players={players
						?.filter((p) => recordsPlayerIds.indexOf(p.id) === -1)
						?.map((p) => {
							return {
								label: p.firstName + " " + p.lastName,
								value: p.id,
								sortKey: p.lastName,
							};
						})}
				/>
			) : null}
		</Page>
	);
}

export default MedicalRecordsPage;
